/**
 * <frame-stack data-substrate="paper|canvas"
 *              data-frame="<colour code>|none"
 *              data-frame-overlays='{"<colour code>":"<overlay-url>", ...}'>
 *
 * The colour code is the tail of a frame's R1-<SIZE><COLOUR> SKU ('S' sort, 'GR' gammelrosa,
 * …). Overlays are per colour, not per size: the artwork is scaled to the poster's aspect.
 *   <img class="frame-stack__art"     src="...">
 *   <img class="frame-stack__texture" hidden src="<canvas-texture-url>">
 *   <img class="frame-stack__overlay" hidden>
 * </frame-stack>
 *
 * Reacts to attribute changes by toggling the texture (canvas substrate) and
 * setting the overlay src (frame). Pure DOM, no fetch, no module deps.
 *
 * INSET MODE (the element also carries `.frame-stack--inset`, i.e. the custom-photo
 * PDP): the overlay PNG's TRANSPARENT OPENING is measured from its alpha channel at
 * runtime and the geometry is published as eight unitless custom properties
 * (--fs-art-x/y/w/h, --fs-ov-x/y/w/h) that custom-photo.css consumes as
 * `calc(var(...) * 100%)`. The art box is fixed (print aspect, centred, the same for
 * every frame) and the OVERLAY is scaled/offset so its opening lands on the art box —
 * so the photo never moves when frames are toggled and is never cropped. If the
 * measurement fails for any reason (CORS-tainted canvas, load error, an overlay served
 * without an alpha channel) the same math runs against reference constants and the
 * preview renders as it did before this file learned to measure.
 *
 * Cover mode (the default, every other PDP) executes exactly the DOM operations it
 * always has: every new code path is gated on the `frame-stack--inset` class.
 */
(function () {
  // ---- inset geometry: constants ------------------------------------------------
  const MEASURE_SIZE   = 512;    // downscale long edge, px
  const ALPHA_EDGE     = 128;    // 0..255 — 50% coverage == the geometric lip edge
  const CENTRE_MAX_A   = 16;     // a pixel this transparent counts as "inside the opening"
  const RAY_COUNT      = 9;      // odd, so the centre line is always sampled
  const RAY_SPREAD     = 0.12;   // rays span 0.38..0.62 of the side
  const MIN_OPENING    = 0.35;   // ow/oh sanity floor
  const MAX_OPENING    = 0.995;  // ow/oh sanity ceiling
  const MAX_INSET      = 0.35;   // any single inset above this is nonsense
  const MIN_INSET      = 0.002;  // any single inset below this means we found the canvas edge

  // Reference opening — doubles as (a) the fallback when measuring fails and (b) the
  // definition of the canonical art-box size, so the art never moves between frames.
  // These are the TRUE authored insets from the frame PNGs' documentation, NOT the
  // 18.8%/6.4% that already had the deliberate under-run baked in.
  const REF_LONG_INSET  = 0.1925;  // inset at the two ends of the SHORTER opening axis
  const REF_SHORT_INSET = 0.0688;  // inset at the two ends of the LONGER opening axis
  const OVERLAP_FRAC    = 0.006;   // art runs 0.6% of each art dimension UNDER the lip, per side
  const MAX_OVERFLOW    = 1.12;    // overlay may bleed at most 12% past the square container
  const DEFAULT_AR      = 2 / 3;   // matches the Liquid default in element.frame-stack.liquid
  const ASPECT_TOLERANCE = 0.08;   // opening-vs-print aspect mismatch that earns a warning

  // Everything below degrades to "do nothing, let the CSS fallbacks render" if the
  // browser is too old for Map / getComputedStyle / canvas.
  const CAN_MEASURE = (function () {
    try {
      return typeof Map === 'function' &&
             typeof getComputedStyle === 'function' &&
             typeof document !== 'undefined' &&
             !!document.createElement('canvas').getContext;
    } catch (err) {
      return false;
    }
  })();

  // url -> Rect | null.  `null` is a first-class value meaning "measurement failed,
  // use the reference rect, never retry and never warn again".
  const OPENING_CACHE   = CAN_MEASURE ? new Map() : null;
  const OPENING_PENDING = CAN_MEASURE ? new Map() : null;
  const ASPECT_WARNED   = CAN_MEASURE ? new Map() : null;

  function clamp(v, lo, hi) {
    return v < lo ? lo : (v > hi ? hi : v);
  }

  function median(list) {
    const s = list.slice().sort(function (a, b) { return a - b; });
    const n = s.length;
    const mid = n >> 1;
    return n % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
  }

  // CSS rotate(90deg) is CLOCKWISE: for an element of local size We x He, a local
  // point (x, y) lands on screen at (He - y, x). Applied to the opening rect that is
  // "the bottom edge swings round to become the left edge".
  function transposeCW(m) {
    return { l: m.b, t: m.l, r: m.t, b: m.r };
  }

  function refOpening(a) {   // a = print aspect = printW / printH
    return a <= 1
      ? { l: REF_LONG_INSET,  t: REF_SHORT_INSET, r: REF_LONG_INSET,  b: REF_SHORT_INSET }   // portrait
      : { l: REF_SHORT_INSET, t: REF_LONG_INSET,  r: REF_SHORT_INSET, b: REF_LONG_INSET };   // landscape
  }

  function printAspect(el) {
    const raw = (getComputedStyle(el).getPropertyValue('--frame-stack-ar') || '').trim();
    if (!raw) return DEFAULT_AR;
    const parts = raw.split('/');
    const w = parseFloat(parts[0]);
    const h = parts.length > 1 ? parseFloat(parts[1]) : 1;
    if (!isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) {
      console.warn('[frame-stack] unparseable --frame-stack-ar:', raw);
      return DEFAULT_AR;
    }
    return w / h;
  }

  // Measure with a PRIVATE Image, never with the displayed overlay: if crossOrigin
  // were set on the visible <img> and the CORS preflight ever failed, the frame would
  // stop rendering at all. Decoupled, a CORS problem degrades geometry only.
  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      const img = new Image();
      img.crossOrigin = 'anonymous';     // MUST be assigned BEFORE .src
      img.decoding = 'async';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('frame overlay failed to load')); };
      img.src = url;
    });
  }

  // Walk from (x0,y0) along (dx,dy) until alpha crosses ALPHA_EDGE. Returns the
  // SUB-PIXEL continuous coordinate of the crossing along the walked axis, or null if
  // the canvas edge is reached first (no opaque border on this ray).
  function walk(A, W, H, x0, y0, dx, dy) {
    if (A(x0, y0) > CENTRE_MAX_A) return null;   // ray starts inside opaque pixels — reject
    let px = x0;
    let py = y0;
    let prevA = A(x0, y0);
    for (;;) {
      const nx = px + dx;
      const ny = py + dy;
      if (nx < 0 || ny < 0 || nx >= W || ny >= H) return null;
      const a = A(nx, ny);
      if (a >= ALPHA_EDGE) {
        // Linear interpolation between the two pixel CENTRES (centre of pixel i is i+0.5).
        const f = (ALPHA_EDGE - prevA) / Math.max(1, a - prevA);
        return dx !== 0 ? px + 0.5 + dx * f
                        : py + 0.5 + dy * f;
      }
      prevA = a;
      px = nx;
      py = ny;
    }
  }

  function isFullyOpaque(data) {
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] !== 255) return false;
    }
    return true;
  }

  // Returns {l,t,r,b} as fractions of the PNG's width (l,r) / height (t,b), or null.
  //
  // A naive alpha bounding box is WRONG: the PNG has TWO transparent regions, the
  // centre opening and the outer drop-shadow margin, and the box returns their union
  // (the whole canvas). Instead cast rays outward from the centre — which is always
  // inside the opening — and take the first alpha crossing on each side. The opaque
  // moulding blocks every ray, so the outer margin is never reached.
  function scan(img) {
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (!nw || !nh) return null;

    const s = MEASURE_SIZE / Math.max(nw, nh);
    const W = Math.max(32, Math.round(nw * s));
    const H = Math.max(32, Math.round(nh * s));

    // FRESH canvas per measurement: a canvas tainted once stays tainted for life, so
    // a module-level scratch canvas would poison every later measurement.
    const c = document.createElement('canvas');
    c.width = W;
    c.height = H;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    // 'high' smoothing turns the PNG's 1-2px anti-aliased edge into a clean linear
    // alpha ramp across one destination pixel — exactly what the interpolation wants.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.clearRect(0, 0, W, H);
    ctx.drawImage(img, 0, 0, W, H);

    let data;
    try {
      data = ctx.getImageData(0, 0, W, H).data;
    } catch (err) {
      return null;   // SecurityError — tainted canvas
    }

    const A = function (x, y) { return data[(y * W + x) * 4 + 3]; };

    // (0) The centre must be INSIDE a transparent opening. A cover-style edge-to-edge
    //     overlay, or an image with no alpha channel at all, fails here and falls back.
    const cx0 = W >> 1;
    const cy0 = H >> 1;
    if (A(cx0, cy0) > CENTRE_MAX_A) {
      if (isFullyOpaque(data)) {
        console.warn('[frame-stack] the frame overlay decoded with NO alpha channel — every pixel ' +
                     'is alpha=255, so there is no opening to find. Most likely the CDN served this ' +
                     'URL in a format without transparency (cdn.shopify.com has been observed ' +
                     'answering "content-type: image/jpeg" for a URL ending in .png), or the asset ' +
                     'really was uploaded as a JPEG. Falling back to the reference insets for', img.src);
      }
      return null;
    }

    const lefts = [];
    const rights = [];
    const tops = [];
    const bottoms = [];
    for (let i = 0; i < RAY_COUNT; i++) {
      const f = 0.5 + RAY_SPREAD * (2 * i / (RAY_COUNT - 1) - 1);   // 0.38 .. 0.62
      const ry = clamp(Math.round(f * H), 0, H - 1);                // row for the horizontal rays
      const rx = clamp(Math.round(f * W), 0, W - 1);                // column for the vertical rays

      let v;
      v = walk(A, W, H, cx0, ry, -1, 0);  if (v !== null) lefts.push(v / W);
      v = walk(A, W, H, cx0, ry, +1, 0);  if (v !== null) rights.push(1 - v / W);
      v = walk(A, W, H, rx, cy0, 0, -1);  if (v !== null) tops.push(v / H);
      v = walk(A, W, H, rx, cy0, 0, +1);  if (v !== null) bottoms.push(1 - v / H);
    }

    // (1) A side that could not be found on at least a majority of its rays is not a frame.
    //     Median (not mean) means a minority of bad rays — a hanging wire, a brand stamp
    //     on the mat, a stray opaque pixel — has literally no effect.
    const need = Math.ceil(RAY_COUNT / 2);
    if (lefts.length < need || rights.length < need || tops.length < need || bottoms.length < need) {
      return null;
    }

    const rect = { l: median(lefts), t: median(tops), r: median(rights), b: median(bottoms) };

    // (2) Sanity. This is the net that catches a degenerate "opening" — e.g. an
    //     alpha-less image whose rays crossed at the very first pixel.
    const ow = 1 - rect.l - rect.r;
    const oh = 1 - rect.t - rect.b;
    if (ow < MIN_OPENING || oh < MIN_OPENING) return null;
    if (ow > MAX_OPENING || oh > MAX_OPENING) return null;
    const sides = [rect.l, rect.t, rect.r, rect.b];
    for (let k = 0; k < sides.length; k++) {
      if (sides[k] < MIN_INSET || sides[k] > MAX_INSET) return null;
    }

    return rect;
  }

  // Promise<Rect|null>, memoised per URL. The key is the exact overlay URL string:
  // one frame colour can carry both a P and an L URL with different openings, and the
  // frame-picker thumbnails are a different width (= a different URL) entirely.
  function measureOpening(url) {
    if (!CAN_MEASURE) return Promise.resolve(null);
    if (OPENING_CACHE.has(url)) return Promise.resolve(OPENING_CACHE.get(url));
    if (OPENING_PENDING.has(url)) return OPENING_PENDING.get(url);

    const p = loadImage(url).then(scan).catch(function () {
      return null;
    }).then(function (res) {
      if (res === null) {
        // Exactly one warning per URL: the null sentinel is cached, so toggling
        // frames back and forth cannot spam the console.
        console.warn('[frame-stack] could not measure the frame opening (canvas tainted, image failed, ' +
                     'or no transparent opening) — falling back to reference insets for', url);
      }
      OPENING_CACHE.set(url, res);
      OPENING_PENDING.delete(url);
      return res;
    });

    OPENING_PENDING.set(url, p);
    return p;
  }

  class FrameStack extends HTMLElement {
    static get observedAttributes() {
      // NOT 'style': relayout() writes the eight custom properties onto this element's
      // own style attribute, so observing it would recurse infinitely. A print-aspect
      // change is signalled by main-product-custom-photo.js calling relayout() directly.
      return ['data-substrate', 'data-frame', 'data-orientation'];
    }

    connectedCallback() {
      this._geoToken = 0;
      this.art = this.querySelector('.frame-stack__art');
      this.texture = this.querySelector('.frame-stack__texture');
      this.overlay = this.querySelector('.frame-stack__overlay');
      this.applySubstrate();
      this.applyFrame();
      this.relayout();
    }

    disconnectedCallback() {
      if (this._overlayResizeObs) {
        this._overlayResizeObs.disconnect();
        this._overlayResizeObs = null;
      }
    }

    attributeChangedCallback(name) {
      if (!this.isConnected) return;
      if (name === 'data-substrate') this.applySubstrate();
      if (name === 'data-frame') this.applyFrame();
      if (name === 'data-orientation') this.applyFrame();
    }

    applySubstrate() {
      if (!this.texture) return;
      const isCanvas = this.getAttribute('data-substrate') === 'canvas';
      this.texture.hidden = !isCanvas;
    }

    applyFrame() {
      if (!this.overlay) return;
      const code = this.getAttribute('data-frame');
      if (!code || code === 'none') {
        this.overlay.hidden = true;
        this.overlay.removeAttribute('src');
        this.applyOverlayRotation(false);
        this.relayout();
        return;
      }
      const map = this.readOverlaysMap();
      const entry = map[code];
      if (entry == null) {
        console.warn('[frame-stack] frame colour not found in data-frame-overlays:', code);
        this.overlay.hidden = true;
        this.applyOverlayRotation(false);
        this.relayout();
        return;
      }
      const requested = (this.getAttribute('data-orientation') || 'P').toUpperCase();
      // Support two shapes:
      //  - flat:   map[code] = "https://…"             (assumed portrait artwork)
      //  - nested: map[code] = { P: "https://…", L: "https://…" }
      let url;
      let used = 'P';  // orientation the chosen PNG was authored for
      if (typeof entry === 'string') {
        url = entry;
        used = 'P';
      } else {
        url = entry[requested];
        if (url) {
          used = requested;
        } else {
          // Fall back to the other orientation if the requested one is missing.
          const fallback = requested === 'L' ? entry.P : entry.L;
          if (fallback) {
            used = requested === 'L' ? 'P' : 'L';
            console.warn('[frame-stack] no overlay for orientation', requested, 'on frame', code, '— falling back to', used, 'and rotating');
            url = fallback;
          } else {
            console.warn('[frame-stack] nested overlay map has no usable URL for frame', code);
            this.overlay.hidden = true;
            this.applyOverlayRotation(false);
            this.relayout();
            return;
          }
        }
      }
      this.overlay.src = url;
      this.overlay.hidden = false;
      // If the PNG's orientation doesn't match the requested one, rotate it 90°
      // so a portrait frame still reads as a correct landscape frame (and vice versa).
      this.applyOverlayRotation(used !== requested);
      this.relayout();
    }

    // Rotate the overlay 90° to cover a container of the opposite orientation.
    // COVER MODE: pure CSS can't set a child's width to its parent's height, so JS sets
    // the pre-rotation box to the container's SWAPPED dimensions and keeps it in sync
    // via a ResizeObserver.
    // INSET MODE: that problem evaporates — the container is square, so a percentage
    // width already resolves against a side length that is also the height. Inset mode
    // therefore carries NO ResizeObserver and nothing recomputes on resize.
    applyOverlayRotation(rotate) {
      if (!this.overlay) return;
      const inset = this.classList.contains('frame-stack--inset');
      if (rotate) {
        this.overlay.classList.add('frame-stack__overlay--rotated');
        if (inset) {
          // Geometry comes from the custom properties; clear any px sizes cover mode left behind.
          this.overlay.style.width = '';
          this.overlay.style.height = '';
          if (this._overlayResizeObs) {
            this._overlayResizeObs.disconnect();
            this._overlayResizeObs = null;
          }
        } else {
          this.syncRotatedOverlaySize();
          if (!this._overlayResizeObs && typeof ResizeObserver === 'function') {
            this._overlayResizeObs = new ResizeObserver(() => this.syncRotatedOverlaySize());
            this._overlayResizeObs.observe(this);
          }
        }
      } else {
        this.overlay.classList.remove('frame-stack__overlay--rotated');
        this.overlay.style.width = '';
        this.overlay.style.height = '';
        if (this._overlayResizeObs) {
          this._overlayResizeObs.disconnect();
          this._overlayResizeObs = null;
        }
      }
    }

    syncRotatedOverlaySize() {
      if (!this.overlay) return;
      const w = this.clientWidth;
      const h = this.clientHeight;
      if (!w || !h) return;  // host still display:none — ResizeObserver re-fires on reveal
      this.overlay.style.width = h + 'px';
      this.overlay.style.height = w + 'px';
    }

    // Publish the inset-mode geometry as eight unitless fractions of the (square)
    // container. No-op in cover mode. Safe to call as often as you like.
    relayout() {
      if (!CAN_MEASURE) return;
      if (!this.classList.contains('frame-stack--inset')) return;   // cover mode: untouched

      // main-product-custom-photo.js may call relayout() on an element that has not run
      // connectedCallback yet; NaN tokens would silently discard every late measurement.
      if (typeof this._geoToken !== 'number') this._geoToken = 0;

      const a = printAspect(this);
      const code = this.getAttribute('data-frame');
      const framed = !!code && code !== 'none' && !!this.overlay && !this.overlay.hidden;
      const url = framed ? (this.overlay.getAttribute('src') || '') : '';
      const rotated = !!this.overlay && this.overlay.classList.contains('frame-stack__overlay--rotated');
      const token = ++this._geoToken;
      const self = this;

      const write = function (rect) {
        const REF = refOpening(a);
        const REFow = 1 - REF.l - REF.r;
        const REFoh = 1 - REF.t - REF.b;

        // Step 1 — the art box. Frame-INDEPENDENT: the print aspect, centred, the same
        // for every frame, so the photo never moves when frames are toggled. Inflated by
        // the overlap so the lip tucks over the art with no hairline.
        let Aw = Math.min(REFow, a * REFoh) / (1 - 2 * OVERLAP_FRAC);
        let Ah = Aw / a;
        let artX = (1 - Aw) / 2;
        let artY = (1 - Ah) / 2;

        let ovW = 0, ovH = 0, ovX = 0, ovY = 0;

        if (url) {
          // A measured rect is in the PNG's own space, so rotate it into screen space.
          // The reference rect is already screen-space (refOpening keys off the print
          // aspect, which is the post-rotation orientation), so it is used as-is.
          const M = rect ? (rotated ? transposeCW(rect) : rect) : REF;
          const Mow = 1 - M.l - M.r;
          const Moh = 1 - M.t - M.b;

          if (rect) {
            // Plausibility only — a measured-but-odd opening still beats a wrong constant,
            // so this warns and carries on rather than falling back.
            const mismatch = Math.abs((Mow / Moh) / a - 1);
            if (mismatch > ASPECT_TOLERANCE && !ASPECT_WARNED.has(url)) {
              ASPECT_WARNED.set(url, true);
              console.warn('[frame-stack] frame opening aspect', (Mow / Moh).toFixed(4),
                'differs from print aspect', a.toFixed(4), 'by', (mismatch * 100).toFixed(1) + '%',
                '— the overlay will be stretched to fit. Check the frame asset:', url);
            }
          }

          // Step 2 — scale and offset the overlay (independently in X and Y) so its
          // opening lands exactly on the art box. Step 3 — clamp the overflow, which
          // can only shrink the art, so one recomputation is always enough.
          for (let pass = 0; pass < 2; pass++) {
            // ONE absolute overlap shared by all four sides. Taking OVERLAP_FRAC of each
            // dimension SEPARATELY (Aw across, Ah down) puts a thicker lip on the long
            // axis; because the printed mat is a uniform pixel inset, a thicker lip there
            // leaves less mat visible top/bottom than left/right — i.e. it reproduces, at
            // ~5%, the exact unequal-border defect this rewrite exists to remove.
            const ov = OVERLAP_FRAC * Math.min(Aw, Ah);
            const openW = Aw - 2 * ov;
            const openH = Ah - 2 * ov;
            ovW = openW / Mow;
            ovH = openH / Moh;
            ovX = artX + ov - M.l * ovW;
            ovY = artY + ov - M.t * ovH;

            const k = Math.max(ovW, ovH);
            if (pass === 0 && k > MAX_OVERFLOW) {
              const scale = MAX_OVERFLOW / k;
              Aw *= scale;
              Ah *= scale;
              artX = (1 - Aw) / 2;
              artY = (1 - Ah) / 2;
              console.info('[frame-stack] chunky frame — art scaled to', (scale * 100).toFixed(1) + '%', url);
              continue;
            }
            break;
          }
        }

        setFrac(self, '--fs-art-x', artX);
        setFrac(self, '--fs-art-y', artY);
        setFrac(self, '--fs-art-w', Aw);
        setFrac(self, '--fs-art-h', Ah);

        if (!url) return;   // no frame: the art box is the whole story

        if (rotated) {
          // --fs-ov-w/h are the PRE-rotation dimensions (the screen box swapped) and
          // --fs-ov-x/y are the SCREEN-space centre, consumed by translate(-50%,-50%).
          setFrac(self, '--fs-ov-w', ovH);
          setFrac(self, '--fs-ov-h', ovW);
          setFrac(self, '--fs-ov-x', ovX + ovW / 2);
          setFrac(self, '--fs-ov-y', ovY + ovH / 2);
        } else {
          setFrac(self, '--fs-ov-w', ovW);
          setFrac(self, '--fs-ov-h', ovH);
          setFrac(self, '--fs-ov-x', ovX);
          setFrac(self, '--fs-ov-y', ovY);
        }
      };

      if (!url) { write(null); return; }                                      // no frame: art box only
      if (OPENING_CACHE.has(url)) { write(OPENING_CACHE.get(url)); return; }  // sync — no flash on re-toggle
      write(null);                                                            // reference geometry meanwhile
      measureOpening(url).then(function (rect) {
        if (token !== self._geoToken) return;   // stale — the customer already switched frames
        write(rect);
      });
    }

    readOverlaysMap() {
      const raw = this.getAttribute('data-frame-overlays');
      if (!raw) return {};
      try {
        return JSON.parse(raw);
      } catch (err) {
        console.warn('[frame-stack] invalid data-frame-overlays JSON:', err);
        return {};
      }
    }
  }

  function setFrac(el, name, value) {
    el.style.setProperty(name, String(Math.round(value * 1e6) / 1e6));
  }

  // Exposed for assets/element.frame-stack.test.html.
  FrameStack.measureOpening = measureOpening;
  FrameStack.OPENING_CACHE = OPENING_CACHE;

  if (!customElements.get('frame-stack')) {
    customElements.define('frame-stack', FrameStack);
  }
})();
